const textValue = (value) => value ?? "";
const idValue = (value) => value == null ? "" : String(value);
const percentValue = (value) => String(value ?? "").replace(/\s*%$/, "").trim();
const numericValue = (value) => {
    if (value == null || value === "") return value ?? null;
    const number = Number(value);
    return Number.isNaN(number) ? value : number;
};

export const placeToFormValues = (place = {}) => ({
    name: textValue(place.name),
    head_name: textValue(place.head_name),
    phone_number: textValue(place.phone_number),
    login: textValue(place.login),
    bin_number: textValue(place.bin_number),
    account_number: textValue(place.account_number),
    off_percent: percentValue(place.off_percent),
    city: idValue(place.city_id ?? place.city),
    category: idValue(place.category_id ?? place.category),
    is_shown: Boolean(place.is_shown),
    bonus_only: Boolean(place.bonus_only),
    created_by: textValue(place.created_by),
    logo: null,
    agreement: null,
    background: null,
});

const PLACE_TEXT_FIELDS = [
    "name",
    "head_name",
    "phone_number",
    "login",
    "bin_number",
    "account_number",
    "city",
    "category",
    "created_by",
];

const PLACE_FILE_FIELDS = ["logo", "agreement", "background"];

export const toPlaceFormData = (values = {}, {mode = "create", includeCreatedBy = true} = {}) => {
    const formData = new FormData();

    PLACE_TEXT_FIELDS.forEach((field) => {
        if (field !== "created_by" || includeCreatedBy) formData.append(field, values[field] ?? "");
    });

    formData.append("off_percent", percentValue(values.off_percent));

    ["is_shown", "bonus_only", "logo_exists", "logo_change"].forEach((field) => {
        if (values[field]) formData.append(field, "true");
    });

    PLACE_FILE_FIELDS.forEach((field) => {
        if (values[field] != null) formData.append(field, values[field]);
    });

    if (mode === "create" && values.password) formData.append("password", values.password);
    return formData;
};

export const branchToFormValues = (branch = {}) => ({
    name: textValue(branch.name),
    admin_name: textValue(branch.admin_name),
    phone_number: textValue(branch.phone_number),
    login: textValue(branch.login),
    bin_number: textValue(branch.bin_number),
    account_number: textValue(branch.account_number),
    off_percent: percentValue(branch.off_percent),
    city: idValue(branch.city_id ?? branch.city),
    category: idValue(branch.category_id ?? branch.category),
    address: textValue(branch.address),
    description: textValue(branch.description),
    lng: textValue(branch.lng),
    lat: textValue(branch.lat),
    is_shown: Boolean(branch.is_shown),
    personal: Array.isArray(branch.personal_set) ? branch.personal_set.map((item) => ({...item})) : [],
    personal_motivation_enabled: Boolean(branch.personal_motivation_enabled),
    personal_reward_percent: textValue(branch.personal_reward_percent),
    staff_attribution_window_minutes: branch.staff_attribution_window_minutes ?? null,
    insta_link: textValue(branch.insta_link),
    wp_link: textValue(branch.wp_link),
    gis_link: textValue(branch.gis_link),
    monday_start: textValue(branch.monday_start),
    monday_end: textValue(branch.monday_end),
    tuesday_start: textValue(branch.tuesday_start),
    tuesday_end: textValue(branch.tuesday_end),
    wednesday_start: textValue(branch.wednesday_start),
    wednesday_end: textValue(branch.wednesday_end),
    thursday_start: textValue(branch.thursday_start),
    thursday_end: textValue(branch.thursday_end),
    friday_start: textValue(branch.friday_start),
    friday_end: textValue(branch.friday_end),
    saturday_start: textValue(branch.saturday_start),
    saturday_end: textValue(branch.saturday_end),
    sunday_start: textValue(branch.sunday_start),
    sunday_end: textValue(branch.sunday_end),
});

export const toBranchPayload = (values = {}, {mode = "create"} = {}) => {
    const payload = Object.fromEntries(
        Object.entries(values).filter(([key]) => key !== "countryScope" && key !== "country_id"),
    );
    if (!payload.password) delete payload.password;
    if (mode === "update") delete payload.personal;

    return {
        ...payload,
        city: numericValue(values.city),
        category: numericValue(values.category),
        off_percent: percentValue(values.off_percent),
        lat: numericValue(values.lat),
        lng: numericValue(values.lng),
        staff_attribution_window_minutes: numericValue(values.staff_attribution_window_minutes),
    };
};

const REFERRAL_DISTRIBUTION_TYPES = new Set(["all_to_place", "all_to_staff", "top_1", "custom"]);

const decimalParts = (value) => {
    const [coefficient, exponentText = "0"] = String(Number(value)).toLowerCase().split("e");
    const [whole, fraction = ""] = coefficient.split(".");
    const exponent = Number(exponentText);
    let digits = `${whole}${fraction}`.replace(/^0+(?=\d)/, "");
    let scale = fraction.length - exponent;
    if (scale < 0) {
        digits += "0".repeat(-scale);
        scale = 0;
    }
    return {digits: digits || "0", scale};
};

const addIntegerStrings = (left, right) => {
    let carry = 0;
    let result = "";
    for (let index = Math.max(left.length, right.length) - 1; index >= 0; index -= 1) {
        const leftIndex = index - (Math.max(left.length, right.length) - left.length);
        const rightIndex = index - (Math.max(left.length, right.length) - right.length);
        const sum = Number(left[leftIndex] || 0) + Number(right[rightIndex] || 0) + carry;
        result = `${sum % 10}${result}`;
        carry = Math.floor(sum / 10);
    }
    return `${carry || ""}${result}`.replace(/^0+(?=\d)/, "");
};

const decimalTotalIsOneHundred = (shares) => {
    const parts = shares.map((share) => decimalParts(share.percent));
    const scale = parts.reduce((maximum, item) => Math.max(maximum, item.scale), 0);
    const total = parts.reduce(
        (sum, item) => addIntegerStrings(sum, `${item.digits}${"0".repeat(scale - item.scale)}`),
        "0",
    );
    return total === `100${"0".repeat(scale)}`;
};

export const referralSettingsToDraft = (settings = {}, staff = []) => ({
    distribution_type: settings.distribution_type || "all_to_place",
    inviter_personal_id: settings.inviter_personal ?? null,
    shares: (settings.shares || []).map((share) => ({
        id: share.id,
        personal_id: share.personal,
        is_place_share: Boolean(share.is_place_share),
        percent: Number(share.percent),
        personal_name: staff.find((item) => item.id === share.personal)?.name || share.personal_name || "",
    })),
});

export const validateReferralDraft = (draft = {}) => {
    const distributionType = draft.distribution_type || "all_to_place";
    if (!REFERRAL_DISTRIBUTION_TYPES.has(distributionType)) {
        return {valid: false, message: "Выберите поддерживаемую схему распределения"};
    }
    if (distributionType !== "custom") return {valid: true, message: ""};

    const shares = Array.isArray(draft.shares) ? draft.shares : [];
    if (shares.length === 0) return {valid: false, message: "Добавьте хотя бы одну долю"};

    const targets = new Set();
    for (const share of shares) {
        const isPlaceShare = Boolean(share.is_place_share);
        const personalId = share.personal_id;
        if (isPlaceShare === (personalId !== null && personalId !== undefined && personalId !== "")) {
            return {valid: false, message: "Для каждой доли выберите филиал или сотрудника"};
        }

        const target = isPlaceShare ? "place" : `personal:${personalId}`;
        if (targets.has(target)) return {valid: false, message: "Получатели долей не должны повторяться"};
        targets.add(target);

        const percent = Number(share.percent);
        if (!Number.isFinite(percent) || percent <= 0 || percent > 100) {
            return {valid: false, message: "Процент доли должен быть больше 0 и не больше 100"};
        }
    }

    if (!decimalTotalIsOneHundred(shares)) {
        return {valid: false, message: "Сумма процентов должна быть равна 100"};
    }
    return {valid: true, message: ""};
};
